import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Input } from "@/components/ui/inputForm";
import { Button } from "@/components/ui/button";
import { message, Select, ConfigProvider, theme as antdTheme, Checkbox, Table, InputNumber } from "antd";
import { getAllPeriodos } from "@/services/periodoLetivo";
import { getUnidades } from "@/services/unidade";
import { getTurmaById, postTurma } from "@/services/turma";
import { getAllEspecificas } from "@/services/especifica";

enum TipoDesconto {
  Especifica1 = "1 Específica",
  Especifica2 = "2 Específicas",
  Especifica3 = "3 Específicas",
  Especifica4 = "4 Específicas",
  Especifica5 = "5 Específicas",
  Especifica6 = "6 Específicas",
  Completo = "Completo",
  CabineEOrientacao = "Cabine/Orientação"
}

interface Desconto {
  id: string;
  tipo: TipoDesconto;
  percentual: number;
}

interface FormData {
  nome: string;
  ativo: boolean;
  naoAplicarFatorUnidade: boolean;
  periodoLetivoId?: number;
  percDescontoMatricula: number;
  unidades: number[];
  especificas: number[];
  descontos: Desconto[];
}

const defaultValues: FormData = {
  nome: "",
  ativo: true,
  naoAplicarFatorUnidade: false,
  periodoLetivoId: undefined,
  percDescontoMatricula: 0,
  unidades: [],
  especificas: [],
  descontos: Object.entries(TipoDesconto).map(([key, label]) => ({
    id: key,
    tipo: label as TipoDesconto,
    percentual: 0,
  })),
};

export default function CadastroTurma() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [periodos, setPeriodos] = useState<any[]>([]);
  const [unidades, setUnidades] = useState<any[]>([]);
  const [especificas, setEspecificas] = useState<any[]>([]);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    control,
    formState: { errors },
    watch,
  } = useForm<FormData>({
    defaultValues,
    mode: "onBlur",
  });

  const watchedDescontos = watch("descontos");

  const updateDesconto = (id: string, percentual: number) => {
    setValue("descontos", (watchedDescontos || []).map(d => 
      d.id === id ? { ...d, percentual } : d
    ));
  };

  const descontoColumns = [
    {
      title: "Tipo de Desconto",
      dataIndex: "tipo",
      key: "tipo",
      render: (value: TipoDesconto) => (
        <span className="text-white">{value}</span>
      ),
    },
    {
      title: "Percentual (%)",
      dataIndex: "percentual",
      key: "percentual",
      render: (value: number, record: Desconto) => (
        <InputNumber
          value={value}
          onChange={(newValue) => updateDesconto(record.id, newValue || 0)}
          min={0}
          max={100}
          style={{ width: "100%" }}
          formatter={(value) => `${value} %`}
          parser={(value) => Number(value?.replace("%", "") || 0)}
        />
      ),
    },
  ];

  useEffect(() => {
    getAllPeriodos().then((res) => {
      if (res.status === 200) setPeriodos(res.data);
    });
    getUnidades().then((res) => {
      if (res.status === 200) setUnidades(res.data);
    });
    getAllEspecificas().then((res) => {
      if (res.status === 200) setEspecificas(res.data.filter((e: any) => e.ativo));
    });
  }, []);

  useEffect(() => {
    if (isEdit && id) {
      getTurmaById(id).then((res) => {
        if (res.status === 200) {
          const turma = res.data;
          
          // Mapear descontos da API para os tipos fixos do enum
          const descontosMapeados = Object.entries(TipoDesconto).map(([key, label]) => {
            const descontoExistente = turma.descontos?.find((d: any) => d.tipoDesconto === key);
            return {
              id: key,
              tipo: label as TipoDesconto,
              percentual: descontoExistente?.percentualDesconto || 0,
            };
          });

          reset({
            ...defaultValues,
            ...turma,
            periodoLetivoId: turma.periodoLetivo?.id,
            percDescontoMatricula: typeof turma.percDescontoMatricula === "number" ? turma.percDescontoMatricula : 0,
            unidades: turma.unidades?.map((u: any) => u.id) || [],
            especificas: turma.especificas?.map((e: any) => e.id) || [],
            ativo: turma.ativo !== false,
            naoAplicarFatorUnidade: !!turma.naoAplicarFatorUnidade,
            descontos: descontosMapeados,
          });
        }
      });
    } else {
      reset(defaultValues);
    }
  }, [id, isEdit, reset]);

  const handleSelectAllEspecificas = () => {
    setValue("especificas", especificas.map((e: any) => e.id) as any);
  };
  const handleClearEspecificas = () => {
    setValue("especificas", [] as any);
  };

  const onSubmit = async (data: any) => {
    try {
      const payload = {
        id: id || undefined,
        nome: data.nome,
        ativo: !!data.ativo,
        naoAplicarFatorUnidade: !!data.naoAplicarFatorUnidade,
        periodoLetivoId: data.periodoLetivoId,
        percDescontoMatricula: Number(data.percDescontoMatricula ?? 0),
        unidades: data.unidades?.map((id: number) => ({ id })) || [],
        especificas: data.especificas?.map((id: number) => ({ id })) || [],
        descontos: data.descontos?.map((d: Desconto) => ({
          tipoDesconto: d.id,
          percentualDesconto: d.percentual
        })) || [],
      };
      const response = await postTurma(payload);
      if (response.status === 200 || response.status === 201) {
        message.success("Turma salva com sucesso!");
        navigate("/turma");
      }
    } catch (error: any) {
      message.error("Erro ao salvar turma");
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-8">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-8">
          <span className="text-white text-2xl">{isEdit ? "Editar Turma" : "Cadastro de Turma"}</span>
        </div>
        <div className="grid grid-cols-2 gap-6">
          <div className="flex items-center mt-6 gap-8 col-span-2">
            <ConfigProvider theme={{ algorithm: [antdTheme.darkAlgorithm] }}>
              <Controller
                control={control}
                name="ativo"
                render={({ field }) => (
                  <Checkbox
                    id="ativo"
                    checked={!!field.value}
                    onChange={e => field.onChange(e.target.checked)}
                  >
                    <label htmlFor="ativo" className="text-white ml-2">Ativo</label>
                  </Checkbox>
                )}
              />
              <Controller
                control={control}
                name="naoAplicarFatorUnidade"
                render={({ field }) => (
                  <Checkbox
                    id="naoAplicarFatorUnidade"
                    checked={!!field.value}
                    onChange={e => field.onChange(e.target.checked)}
                  >
                    <label htmlFor="naoAplicarFatorUnidade" className="text-white ml-2">Não aplicar fator de unidade</label>
                  </Checkbox>
                )}
              />
            </ConfigProvider>
          </div>
          <div className="col-span-2">
            <label>Nome *</label>
            <Input type="text" placeholder="Nome" {...register("nome", { required: "Nome é obrigatório" })} />
            {errors.nome && <span className="text-red-600">{errors.nome.message}</span>}
          </div>
          <div className="col-span-2">
            <label>Período Letivo *</label>
            <ConfigProvider theme={{ algorithm: [antdTheme.darkAlgorithm, antdTheme.compactAlgorithm] }}>
              <Controller
                control={control}
                name="periodoLetivoId"
                rules={{ required: "Selecione um período letivo" }}
                render={({ field }) => (
                  <Select
                    className="w-full h-10"
                    placeholder="Selecione o período letivo"
                    value={field.value}
                    onChange={field.onChange}
                    options={periodos.map((p) => ({
                      value: p.id,
                      label: `${p.nome} (${p.dataInicio ? new Date(p.dataInicio).toLocaleDateString() : "-"} a ${p.dataFim ? new Date(p.dataFim).toLocaleDateString() : "-"})`,
                    }))}
                  />
                )}
              />
            </ConfigProvider>
            {errors.periodoLetivoId && <span className="text-red-600">{errors.periodoLetivoId.message}</span>}
          </div>
          <div className="col-span-2">
            <label>Unidade *</label>
            <ConfigProvider theme={{ algorithm: [antdTheme.darkAlgorithm, antdTheme.compactAlgorithm] }}>
              <Controller
                control={control}
                name="unidades"
                rules={{ required: "Selecione ao menos uma unidade" }}
                render={({ field }) => (
                  <Select
                    mode="multiple"
                    allowClear
                    className="w-full h-10"
                    placeholder="Selecione as unidades"
                    value={field.value}
                    onChange={field.onChange}
                    options={unidades.map((u) => ({ value: u.id, label: u.nome }))}
                  />
                )}
              />
            </ConfigProvider>
            {errors.unidades && <span className="text-red-600">{errors.unidades.message}</span>}
          </div>
          <div className="col-span-2">
            <label>Percentual de Desconto da Matrícula *</label>
            <ConfigProvider theme={{ algorithm: [antdTheme.darkAlgorithm, antdTheme.compactAlgorithm] }}>
              <Controller
                control={control}
                name="percDescontoMatricula"
                rules={{
                  required: "Informe o percentual de desconto da matrícula",
                  min: { value: 0, message: "O valor mínimo é 0" },
                  max: { value: 100, message: "O valor máximo é 100" },
                  validate: (value) => Number.isInteger((Number(value) || 0) * 100) || "Use no máximo 2 casas decimais",
                }}
                render={({ field }) => (
                  <InputNumber
                    className="w-full h-10"
                    min={0}
                    max={100}
                    step={0.01}
                    precision={2}
                    placeholder="Ex.: 25,50"
                    value={field.value}
                    onChange={(value) => field.onChange(typeof value === "number" ? value : 0)}
                    formatter={(value) => `${(value ?? "").toString().replace(".", ",")} %`}
                    parser={(value) => Number((value ?? "").replace(",", ".").replace("%", "") || 0)}
                  />
                )}
              />
            </ConfigProvider>
            {errors.percDescontoMatricula && <span className="text-red-600">{errors.percDescontoMatricula.message}</span>}
          </div>
          <div className="col-span-2">
            <label>Específicas</label>
            <ConfigProvider theme={{ algorithm: [antdTheme.darkAlgorithm, antdTheme.compactAlgorithm] }}>
              <Controller
                control={control}
                name="especificas"
                render={({ field }) => (
                  <div 
                    className="grid grid-cols-3 gap-4"
                    style={{ maxHeight: 200, overflowY: "auto", background: "#141414" }}
                  >
                    {especificas.map(item => (
                      <div key={item.id} className="flex items-center p-2 border border-gray-600 rounded">
                        <Checkbox
                          checked={field.value?.includes(item.id)}
                          onChange={e => {
                            const checked = e.target.checked;
                            let newValue = Array.isArray(field.value) ? [...field.value] : [];
                            if (checked) {
                              newValue.push(item.id);
                            } else {
                              newValue = newValue.filter((id: number) => id !== item.id);
                            }
                            field.onChange(newValue);
                          }}
                        >
                          {item.nome}
                        </Checkbox>
                      </div>
                    ))}
                  </div>
                )}
              />
            </ConfigProvider>
            <div className="flex gap-4 mt-2">
              <button type="button" className="text-blue-500 text-sm underline hover:text-blue-400 transition" onClick={handleSelectAllEspecificas}>
                Selecionar todas
              </button>
              <button type="button" className="text-blue-500 text-sm underline hover:text-blue-400 transition" onClick={handleClearEspecificas}>
                Remover todas
              </button>
            </div>
          </div>
          <div className="col-span-2">
            <label>Descontos</label>
            <div className="mt-2">
              <ConfigProvider theme={{ algorithm: [antdTheme.darkAlgorithm, antdTheme.compactAlgorithm] }}>
                <Table
                  columns={descontoColumns}
                  dataSource={watchedDescontos}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  style={{ background: "#141414" }}
                />
              </ConfigProvider>
            </div>
          </div>
        </div>
        <div className="flex justify-start p-2 mt-12 gap-8">
          <Button
            className="flex gap-2"
            variant="destructive"
            type="button"
            onClick={() => navigate("/turma")}
          >
            Cancelar
          </Button>
          <Button className="flex gap-2" type="submit">
            Salvar
          </Button>
        </div>
      </form>
    </div>
  );
} 